import { CARD_ELEVATION } from '@/components/page-card.styles';
import { ScrollArea } from '@/components/ui/scroll-area';
import { TooltipProvider } from '@/components/ui/tooltip';
import { WhatsNewCard } from '@/components/whats-new/whats-new-card.component';
import { cn } from '@/lib/utils';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { useSidebar } from '../sidebar-provider.component';
import { ChatTabs } from './chat-tabs.component';
import { publishCluOpener, type CluOpener } from './clu-opener.store';
import { PageRail } from './page-rail.component';
import { SidebarFooter } from './sidebar-footer.component';
import { SidebarDnd } from './sidebar-dnd.component';
import {
  focusCandidates,
  focusFirst,
  refocusAfterRender,
} from './sidebar-focus.util';
import { SidebarPinsContext, type SidebarPins } from './sidebar-menu.util';
import { publishSidebarPins } from './sidebar-pins.store';
import { CommandBar, FavoriteTiles, PinnedRows } from './sidebar-nav.component';
import { TabsSidebarHeader } from './tabs-sidebar-header.component';
import { useTabsNav } from './use-tabs-nav.hook';

/**
 * The tabbed sidebar, modeled on Arc: the user's pinned pages, agents and
 * chats as tiles and rows, their other chats with Clu and with agents as
 * tabs, and what a top bar would hold. Fixed to the left edge, 256px open and
 * 64px folded, on the shared collapse state.
 *
 * Folded, it is the page rail: the pages by section and Clu in place of the
 * pins and chat tabs, and every control one cell on the rail's centre line
 * (see PageRail).
 */
export function TabsSidebar() {
  const { isCollapsed } = useSidebar();
  const nav = useTabsNav();
  const {
    favorites,
    pinned,
    selectedKey,
    pageTab,
    pageTabActive,
    settingsActive,
    missingPages,
    layout,
    chats,
    onLockedClick,
  } = nav;
  // Focus that follows an item a menu or a keyboard drop moves or removes.
  const asideRef = useRef<HTMLElement>(null);
  const heldFocus = useRef<string[] | null>(null);
  const root = useCallback(() => asideRef.current ?? document, []);
  const holdFocus = useCallback(
    (key: string) => {
      heldFocus.current = focusCandidates(key, root());
    },
    [root]
  );
  const restoreFocus = useCallback(() => {
    const candidates = heldFocus.current;
    if (!candidates) return false;
    heldFocus.current = null;
    if (!focusFirst(candidates, root())) refocusAfterRender(candidates, root);
    return true;
  }, [root]);
  const pinActions = useMemo<SidebarPins>(
    () => ({
      layout,
      missingPages,
      closeChat: chats.close,
      reopenChats: chats.reopen,
      holdFocus,
      restoreFocus,
    }),
    [chats.close, chats.reopen, holdFocus, layout, missingPages, restoreFocus]
  );
  // The same actions for the Chats rail, which sits outside the sidebar:
  // one layout, one save queue.
  useLayoutEffect(() => {
    publishSidebarPins(pinActions);
  }, [pinActions]);
  useLayoutEffect(() => () => publishSidebarPins(null), []);
  // Opening Clu the way the tabs do, for the ⌘K palette.
  const cluOpener = useMemo<CluOpener | null>(
    () =>
      chats.available
        ? {
            open: chats.openClu,
            ask: chats.askClu,
            inDock: chats.cluOpensInDock,
          }
        : null,
    [chats.askClu, chats.available, chats.cluOpensInDock, chats.openClu]
  );
  useLayoutEffect(() => {
    publishCluOpener(cluOpener);
  }, [cluOpener]);
  useLayoutEffect(() => () => publishCluOpener(null), []);
  const showTabs = chats.available || pageTab !== null;
  const pins = {
    selectedKey,
    runningChatIds: chats.runningIds,
    onOpenClu: chats.openCluChat,
    onRenameChat: chats.rename,
    onLockedClick,
  };

  return (
    <SidebarPinsContext.Provider value={pinActions}>
      <TooltipProvider delayDuration={0}>
        <aside
          ref={asideRef}
          aria-label='Sidebar'
          data-sidebar-tabs
          className={cn(
            // Below the shell's offer bar (--app-banner-h, 0px without one).
            // z-41, over the Clu dock's frame (z-40, and after this in the
            // DOM, so it wins a tie): folded, the What's new card pops out
            // beside the rail, over the dock, and would be painted under it.
            'fixed top-[var(--app-banner-h,0px)] left-0 z-41 flex h-[calc(100vh-var(--app-banner-h,0px))] flex-col',
            'bg-[hsl(var(--sidebar-tint))] text-sm text-foreground',
            'transition-[width] duration-200 ease-out',
            isCollapsed ? 'w-16 items-center p-2' : 'w-[256px] px-2 pt-2 pb-1.5'
          )}
        >
          <TabsSidebarHeader />
          <CommandBar collapsed={isCollapsed} />
          {isCollapsed ? (
            <PageRail nav={nav} />
          ) : (
            /* Everything between the command bar and the footer scrolls;
               the inner padding keeps the raised cards' shadows unclipped.
               The area starts a little below the command bar and fades its
               top 8px (empty at rest), so scrolled tiles fade out in the gap
               instead of being cut at the bar's edge (Lev, 2026-10-28). */
            <ScrollArea className='-mx-2 mt-1.5 min-h-0 flex-1 [mask-image:linear-gradient(to_bottom,transparent,black_8px)]'>
              <SidebarDnd favorites={favorites} pinned={pinned}>
                <div className='px-2 pt-2.5 pb-1'>
                  <FavoriteTiles items={favorites} {...pins} />
                  <div className='mt-2.5'>
                    <PinnedRows items={pinned} {...pins} />
                  </div>
                  {showTabs && (
                    <ChatTabs
                      state={chats}
                      pageTab={pageTab}
                      pageTabActive={pageTabActive}
                    />
                  )}
                </div>
              </SidebarDnd>
            </ScrollArea>
          )}
          <WhatsNewCard collapsed={isCollapsed} />
          <SidebarFooter
            chatsAvailable={chats.available}
            collapsed={isCollapsed}
            settingsActive={settingsActive}
          />
          {/* The left shadow of the card beside the sidebar: the page card,
              or Clu's dock card while it is open. The sidebar paints over
              that edge, so it draws the shadow itself: the card's shape and
              elevation, just past the sidebar, clipped to its left so only
              that side shows. */}
          <span
            aria-hidden
            data-card-edge
            className={cn(
              'pointer-events-none absolute top-2 bottom-2 left-full w-6 rounded-l-xl [clip-path:inset(-24px_100%_-24px_-24px)] touch-only:hidden',
              CARD_ELEVATION
            )}
          />
        </aside>
      </TooltipProvider>
    </SidebarPinsContext.Provider>
  );
}
